import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HardDrive, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmt = (b) => {
  if (!b) return "0 MB";
  const gb = b / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${(b / 1024 ** 2).toFixed(1)} MB`;
};
const money = (usd) => (usd > 0 ? `$${usd.toFixed(2)}` : "$0.00 (free tier)");

const Stat = ({ label, value, testid }) => (
  <div>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="font-display text-lg font-bold text-slate-900" data-testid={testid}>{value}</div>
  </div>
);

export const StorageCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/files/storage/summary").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const migrate = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/files/storage/migrate");
      toast.success(`Copied ${data.migrated ?? 0} photo(s) to R2${data.failed ? `, ${data.failed} failed` : ""}`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!s) return null;
  const pct = Math.min(100, (s.bytes / s.freeBytes) * 100);
  return (
    <Card className="p-5 space-y-4" data-testid="storage-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <HardDrive className="h-5 w-5 text-brand" />
          <h3 className="font-display font-bold text-slate-900">Photo storage</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${s.r2Enabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="storage-provider-badge">
            {s.r2Enabled ? "Cloudflare R2 · private" : "R2 not connected"}
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={migrate} disabled={busy} data-testid="storage-migrate-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-1">Copy old photos now</span>
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Photos" value={s.photos.toLocaleString()} testid="storage-photo-count" />
        <Stat label="Total stored" value={fmt(s.bytes)} testid="storage-total" />
        <Stat label="Added last 30 days" value={fmt(s.last30DaysBytes)} testid="storage-last30" />
        <Stat label="Est. cost this month" value={money(s.estimatedMonthlyUsd)} testid="storage-cost" />
      </div>
      <div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-slate-500 mt-1" data-testid="storage-projection">
          {pct.toFixed(1)}% of the 10 GB free allowance · at this rate in 12 months: {fmt(s.projected12MonthBytes)} ≈ {money(s.projected12MonthMonthlyUsd)}/month
        </p>
        {(s.pendingMigration > 0 || s.migrationFailed > 0) && (
          <p className="text-xs text-amber-700 mt-1" data-testid="storage-pending">
            {s.pendingMigration} file(s) still on old storage{s.migrationFailed ? ` · ${s.migrationFailed} failed to copy (will retry)` : ""}
          </p>
        )}
      </div>
    </Card>
  );
};
